import React, { useEffect, useState, useMemo } from 'react';
import { Grid, Box, Paper, Typography, Avatar, Tab, Tabs, TextField } from '@mui/material';
import Select from 'react-select';
import { Send, User } from 'lucide-react';
import moment from 'moment';
import * as actions from './actions';
import { toastError, toastSuccess, getTokenPayload } from '../../utils';
import { API_URL } from '../../constants';
import darkSelectStyles from '../../theme/reactSelectStyles';
import ActionButton from '../../components/ActionButton';
import ConfirmModal from '../Common/CommonModal';
import LeaveCalendar from './LeaveCalendar';
import UpcomingLeaves from './UpcomingLeaves';
import MyLeaveHistory from './MyLeaveHistory';
import LeaveHistory from './LeaveHistory';

const SUPER_ADMIN_EMAILS = [
  'harjaspreet.singh@ambalaproductions.in',
  'vijay.kumar@ambalaproductions.in',
];

const leaveTypeOptions = [
  { value: 'full_day', label: 'Full Day' },
  { value: 'half_day_morning', label: 'Half Day - Morning' },
  { value: 'half_day_afternoon', label: 'Half Day - Afternoon' },
  { value: 'short_1hr', label: 'Short Leave - 1 Hour' },
  { value: 'short_1_5hr', label: 'Short Leave - 1.5 Hours' },
  { value: 'short_2hr', label: 'Short Leave - 2 Hours' },
  { value: 'short_2_5hr', label: 'Short Leave - 2.5 Hours' },
  { value: 'short_3hr', label: 'Short Leave - 3 Hours' },
];

const shortLeaveTypes = ['short_1hr', 'short_1_5hr', 'short_2hr', 'short_2_5hr', 'short_3hr'];

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

export default function Profile(props) {
  const params = new URLSearchParams(props.location?.search || '');
  const isSuperAdmin = useMemo(() => {
    const email = getTokenPayload()?.email;
    return email && SUPER_ADMIN_EMAILS.includes(email);
  }, []);
  const [profile, setProfile] = useState({ name: '', email: '', photograph: '' });
  const [activeTab, setActiveTab] = useState(params.get('tab') === 'leaves' ? 2 : 0);
  const [leaveDate, setLeaveDate] = useState(new Date());
  const [leaveType, setLeaveType] = useState(leaveTypeOptions[3]);
  const [leaveTime, setLeaveTime] = useState(null);
  const [reason, setReason] = useState('');
  const [blockedDates, setBlockedDates] = useState({});
  const [applying, setApplying] = useState(false);
  const [calendarRefreshKey, setCalendarRefreshKey] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const isShortLeave = shortLeaveTypes.includes(leaveType?.value);

  const isTodayOrTomorrow = useMemo(() => {
    if (!leaveDate) return false;
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const sel = new Date(leaveDate);
    sel.setHours(0, 0, 0, 0);
    const diffDays = Math.round((sel - now) / (1000 * 60 * 60 * 24));
    return diffDays <= 1;
  }, [leaveDate]);

  useEffect(() => {
    document.title = 'MP | My Profile';
    loadProfile();
    loadBlockedDates();
    return () => { document.title = 'Fronic Media Admin'; };
  }, []);

  useEffect(() => {
    const p = new URLSearchParams(props.location?.search || '');
    if (p.get('tab') === 'leaves') setActiveTab(2);
  }, [props.location?.search]);

  const loadProfile = () => {
    actions.getAdminProfile()
      .then((res) => setProfile(res.body || {}))
      .catch((err) => toastError(err?.message || 'Failed to load profile'));
  };

  const loadBlockedDates = () => {
    actions.getBlockedDates()
      .then((res) => setBlockedDates(res.body || {}))
      .catch(() => {});
  };

  const onCalendarDateClick = (date) => {
    setLeaveDate(date);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const sel = new Date(date);
    sel.setHours(0, 0, 0, 0);
    const diffDays = Math.round((sel - now) / (1000 * 60 * 60 * 24));
    if (diffDays <= 1 && !shortLeaveTypes.includes(leaveType?.value)) {
      setLeaveType(leaveTypeOptions[3]); // Short Leave - 1 Hour
    } else if (diffDays > 1 && shortLeaveTypes.includes(leaveType?.value)) {
      setLeaveType(leaveTypeOptions[0]); // Full Day
      setLeaveTime(null);
    }
  };

  const onApplyLeave = () => {
    if (!leaveDate) {
      toastError('Please select a date from the calendar');
      return;
    }
    if (isShortLeave && !leaveTime) {
      toastError('Please select a time for the short leave');
      return;
    }
    if (!reason.trim()) {
      toastError('Please enter a reason');
      return;
    }
    setConfirmOpen(true);
  };

  const formatLeaveTime = (timeVal) => {
    if (!timeVal) return null;
    const h = String(timeVal.getHours()).padStart(2, '0');
    const m = String(timeVal.getMinutes()).padStart(2, '0');
    return `${h}:${m}`;
  };

  const onConfirmApply = () => {
    setConfirmOpen(false);
    const leaveDateStr = `${leaveDate.getFullYear()}-${String(leaveDate.getMonth() + 1).padStart(2, '0')}-${String(leaveDate.getDate()).padStart(2, '0')}`;

    const payload = {
      leaveDate: leaveDateStr,
      leaveType: leaveType?.value || 'full_day',
      reason: reason.trim(),
    };

    if (isShortLeave && leaveTime) {
      payload.leaveTime = formatLeaveTime(leaveTime);
    }

    setApplying(true);
    props.setLoader(true);
    actions.applyLeave(payload)
      .then((res) => {
        setApplying(false);
        props.setLoader(false);
        toastSuccess(res.message || 'Leave applied successfully');
        setLeaveDate(null);
        setReason('');
        setLeaveType(leaveTypeOptions[0]);
        setLeaveTime(null);
        loadBlockedDates();
        setCalendarRefreshKey(k => k + 1);
      })
      .catch((err) => {
        setApplying(false);
        props.setLoader(false);
        toastError(err?.message || 'Failed to apply leave');
      });
  };

  return (
    <Grid container spacing={3}>
      {/* Profile Card */}
      <Grid item xs={12}>
        <Paper sx={{
          p: 3,
          backgroundColor: '#1A1D27',
          borderRadius: '12px',
          border: '1px solid #2E3247',
          display: 'flex',
          alignItems: 'center',
          gap: 3,
        }}>
          <Avatar
            src={profile.photograph ? `${API_URL}/${profile.photograph}` : undefined}
            sx={{
              width: 72,
              height: 72,
              backgroundColor: '#6C63FF',
              fontSize: '1.8rem',
              fontWeight: 600,
            }}
          >
            {profile.name ? profile.name.charAt(0).toUpperCase() : <User size={32} />}
          </Avatar>
          <Box>
            <Typography sx={{ fontSize: '1.3rem', color: '#F1F5F9', fontWeight: 600 }}>
              {profile.name || 'Admin'}
            </Typography>
            <Typography sx={{ fontSize: '0.88rem', color: '#94A3B8', mt: 0.3 }}>
              {profile.email || ''}
            </Typography>
          </Box>
        </Paper>
      </Grid>

      {/* Tabs */}
      <Grid item xs={12}>
        <Paper sx={{
          backgroundColor: '#1A1D27',
          borderRadius: '12px',
          border: '1px solid #2E3247',
          overflow: 'hidden',
        }}>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            sx={{
              borderBottom: '1px solid #2E3247',
              '& .MuiTab-root': {
                color: '#64748B',
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.88rem',
                minHeight: 48,
                '&.Mui-selected': { color: '#6C63FF' },
              },
              '& .MuiTabs-indicator': { backgroundColor: '#6C63FF' },
            }}
          >
            <Tab label="Calendar & Apply Leave" />
            <Tab label="Upcoming Leaves" />
            <Tab label="My Leaves" />
            {isSuperAdmin && <Tab label="Leave History" />}
          </Tabs>

          <Box sx={{ p: 3 }}>
            {/* Tab 0: Calendar + Apply Leave merged */}
            <TabPanel value={activeTab} index={0}>
              <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {/* Calendar - left side */}
                <Box sx={{ flex: '1 1 520px', minWidth: 0 }}>
                  <LeaveCalendar
                    setLoader={props.setLoader}
                    onDateClick={onCalendarDateClick}
                    selectedDate={leaveDate}
                    refreshKey={calendarRefreshKey}
                    isShortLeave={isShortLeave}
                  />
                </Box>

                {/* Apply Leave form - right side */}
                <Box sx={{
                  flex: '0 0 320px',
                  minWidth: 280,
                  p: 2.5,
                  borderRadius: '10px',
                  backgroundColor: '#1E2130',
                  border: '1px solid #2E3247',
                  alignSelf: 'flex-start',
                }}>
                  <Typography sx={{ fontSize: '0.95rem', color: '#F1F5F9', fontWeight: 600, mb: 2.5 }}>
                    Apply Leave
                  </Typography>

                  {/* Selected date display */}
                  <Box sx={{ mb: 2.5 }}>
                    <Typography sx={{ fontSize: '0.78rem', color: '#94A3B8', mb: 0.8, fontWeight: 500 }}>
                      Selected Date
                    </Typography>
                    <Box sx={{
                      p: 1.2,
                      borderRadius: '8px',
                      backgroundColor: '#232636',
                      border: leaveDate ? '1px solid rgba(108, 99, 255, 0.3)' : '1px solid #2E3247',
                    }}>
                      <Typography sx={{
                        fontSize: '0.85rem',
                        color: leaveDate ? '#F1F5F9' : '#64748B',
                        fontWeight: leaveDate ? 500 : 400,
                      }}>
                        {leaveDate ? moment(leaveDate).format('ddd, DD MMM YYYY') : 'Click a date on the calendar'}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ mb: 2.5 }}>
                    <Typography sx={{ fontSize: '0.78rem', color: '#94A3B8', mb: 0.8, fontWeight: 500 }}>
                      Leave Type
                    </Typography>
                    <Select
                      value={leaveType}
                      onChange={(val) => {
                        setLeaveType(val);
                        if (!shortLeaveTypes.includes(val?.value)) {
                          setLeaveTime(null);
                        }
                      }}
                      options={isTodayOrTomorrow ? leaveTypeOptions.filter(opt => shortLeaveTypes.includes(opt.value)) : leaveTypeOptions.filter(opt => !shortLeaveTypes.includes(opt.value))}
                      styles={darkSelectStyles}
                      isSearchable={false}
                      menuPortalTarget={document.body}
                    />
                  </Box>

                  {isShortLeave && (
                    <Box sx={{ mb: 2.5 }}>
                      <Typography sx={{ fontSize: '0.78rem', color: '#94A3B8', mb: 0.8, fontWeight: 500 }}>
                        Leave Time
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box component="select"
                          value={leaveTime ? (leaveTime.getHours() % 12 || 12) : ''}
                          onChange={(e) => {
                            const h12 = parseInt(e.target.value);
                            const d = leaveTime ? new Date(leaveTime) : new Date();
                            const isPM = leaveTime ? leaveTime.getHours() >= 12 : (new Date().getHours() >= 12);
                            d.setHours(isPM ? (h12 === 12 ? 12 : h12 + 12) : (h12 === 12 ? 0 : h12));
                            d.setMinutes(leaveTime ? leaveTime.getMinutes() : 0);
                            d.setSeconds(0);
                            setLeaveTime(d);
                          }}
                          sx={{
                            flex: 1, height: 40, borderRadius: '8px', border: '1px solid #2E3247',
                            backgroundColor: '#232636', color: '#F1F5F9', fontSize: '0.85rem',
                            px: 1.2, outline: 'none', cursor: 'pointer',
                            '&:hover': { borderColor: '#6C63FF' },
                            '&:focus': { borderColor: '#6C63FF' },
                            '& option': { backgroundColor: '#232636', color: '#F1F5F9' },
                          }}
                        >
                          <option value="" disabled>Hr</option>
                          {[12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(h => (
                            <option key={h} value={h}>{String(h).padStart(2, '0')}</option>
                          ))}
                        </Box>
                        <Typography sx={{ color: '#64748B', fontSize: '1.1rem', fontWeight: 600 }}>:</Typography>
                        <Box component="select"
                          value={leaveTime ? leaveTime.getMinutes() : ''}
                          onChange={(e) => {
                            const d = leaveTime ? new Date(leaveTime) : new Date();
                            if (!leaveTime) { d.setHours(new Date().getHours()); }
                            d.setMinutes(parseInt(e.target.value));
                            d.setSeconds(0);
                            setLeaveTime(d);
                          }}
                          sx={{
                            flex: 1, height: 40, borderRadius: '8px', border: '1px solid #2E3247',
                            backgroundColor: '#232636', color: '#F1F5F9', fontSize: '0.85rem',
                            px: 1.2, outline: 'none', cursor: 'pointer',
                            '&:hover': { borderColor: '#6C63FF' },
                            '&:focus': { borderColor: '#6C63FF' },
                            '& option': { backgroundColor: '#232636', color: '#F1F5F9' },
                          }}
                        >
                          <option value="" disabled>Min</option>
                          {[0, 15, 30, 45].map(m => (
                            <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
                          ))}
                        </Box>
                        <Box sx={{ display: 'flex', borderRadius: '8px', overflow: 'hidden', border: '1px solid #2E3247' }}>
                          <Box
                            onClick={() => {
                              const d = leaveTime ? new Date(leaveTime) : new Date();
                              if (!leaveTime) { d.setMinutes(0); d.setSeconds(0); }
                              const h = d.getHours();
                              if (h >= 12) d.setHours(h - 12);
                              setLeaveTime(new Date(d));
                            }}
                            sx={{
                              px: 1.2, py: 0.8, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
                              transition: 'all 0.15s ease',
                              backgroundColor: leaveTime && leaveTime.getHours() < 12 ? '#6C63FF' : '#232636',
                              color: leaveTime && leaveTime.getHours() < 12 ? '#FFFFFF' : '#94A3B8',
                              '&:hover': {
                                backgroundColor: leaveTime && leaveTime.getHours() < 12 ? '#5B54E0' : '#2A2D3E',
                              },
                            }}
                          >
                            AM
                          </Box>
                          <Box
                            onClick={() => {
                              const d = leaveTime ? new Date(leaveTime) : new Date();
                              if (!leaveTime) { d.setMinutes(0); d.setSeconds(0); }
                              const h = d.getHours();
                              if (h < 12) d.setHours(h + 12);
                              setLeaveTime(new Date(d));
                            }}
                            sx={{
                              px: 1.2, py: 0.8, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
                              transition: 'all 0.15s ease',
                              borderLeft: '1px solid #2E3247',
                              backgroundColor: leaveTime && leaveTime.getHours() >= 12 ? '#6C63FF' : '#232636',
                              color: leaveTime && leaveTime.getHours() >= 12 ? '#FFFFFF' : '#94A3B8',
                              '&:hover': {
                                backgroundColor: leaveTime && leaveTime.getHours() >= 12 ? '#5B54E0' : '#2A2D3E',
                              },
                            }}
                          >
                            PM
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  )}

                  <Box sx={{ mb: 2.5 }}>
                    <Typography sx={{ fontSize: '0.78rem', color: '#94A3B8', mb: 0.8, fontWeight: 500 }}>
                      Reason
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      placeholder="Enter reason for leave..."
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#232636',
                          color: '#F1F5F9',
                          fontSize: '0.85rem',
                          '& fieldset': { borderColor: '#2E3247' },
                          '&:hover fieldset': { borderColor: '#6C63FF' },
                          '&.Mui-focused fieldset': { borderColor: '#6C63FF' },
                        },
                        '& .MuiInputBase-input::placeholder': { color: '#64748B', opacity: 1 },
                      }}
                    />
                  </Box>

                  <ActionButton
                    variant="settle"
                    onClick={onApplyLeave}
                    disabled={applying || !leaveDate}
                    sx={{ width: '100%' }}
                  >
                    <Send size={15} style={{ marginRight: 8 }} />
                    Apply Leave
                  </ActionButton>
                </Box>
              </Box>
            </TabPanel>

            {/* Tab 1: Upcoming Leaves */}
            <TabPanel value={activeTab} index={1}>
              <UpcomingLeaves setLoader={props.setLoader} />
            </TabPanel>

            {/* Tab 2: My Leaves */}
            <TabPanel value={activeTab} index={2}>
              <MyLeaveHistory setLoader={props.setLoader} />
            </TabPanel>

            {/* Tab 3: Leave History (Super Admin only) */}
            {isSuperAdmin && (
              <TabPanel value={activeTab} index={3}>
                <LeaveHistory setLoader={props.setLoader} />
              </TabPanel>
            )}
          </Box>
        </Paper>
      </Grid>
      <ConfirmModal
        open={confirmOpen}
        title="Confirm Leave Application"
        message={
          <Box>
            <Typography sx={{ fontSize: '0.88rem', color: '#CBD5E1', mb: 2 }}>
              Are you sure you want to apply for leave with the following details?
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
              <Box display="flex" gap={1}>
                <Typography sx={{ fontSize: '0.82rem', color: '#94A3B8', fontWeight: 600, minWidth: 80 }}>Date :</Typography>
                <Typography sx={{ fontSize: '0.82rem', color: '#F1F5F9' }}>
                  {leaveDate ? moment(leaveDate).format('ddd, DD MMM YYYY') : '—'}
                </Typography>
              </Box>
              <Box display="flex" gap={1}>
                <Typography sx={{ fontSize: '0.82rem', color: '#94A3B8', fontWeight: 600, minWidth: 80 }}>Type :</Typography>
                <Typography sx={{ fontSize: '0.82rem', color: '#F1F5F9' }}>
                  {leaveType?.label || 'Full Day'}
                </Typography>
              </Box>
              {isShortLeave && leaveTime && (
                <Box display="flex" gap={1}>
                  <Typography sx={{ fontSize: '0.82rem', color: '#94A3B8', fontWeight: 600, minWidth: 80 }}>Time :</Typography>
                  <Typography sx={{ fontSize: '0.82rem', color: '#F1F5F9' }}>
                    {moment(leaveTime).format('hh:mm A')}
                  </Typography>
                </Box>
              )}
              <Box display="flex" gap={1}>
                <Typography sx={{ fontSize: '0.82rem', color: '#94A3B8', fontWeight: 600, minWidth: 80 }}>Reason :</Typography>
                <Typography sx={{ fontSize: '0.82rem', color: '#F1F5F9' }}>
                  {reason}
                </Typography>
              </Box>
            </Box>
          </Box>
        }
        onSubmitConfirm={onConfirmApply}
        handleClose={() => setConfirmOpen(false)}
      />
    </Grid>
  );
}
